import React, { useState, useEffect, useCallback } from 'react';
import '../../../../assets/css/purchase/datatable.css';

// Components
import Breadcrumbs from '../../../components/Breadcrumbs';
import Topbar from '../../../components/Topbar';
import FileModal from '../../../components/Modal/FileModal';

// Date & Time
import moment from 'moment';

// Utils
import { getAlert, formatCurrency, parseCurrency, getDataWithComp, getData } from '../../../../utils/SamuiUtils';
import { formatDate } from '../../../../utils/DateUtils';
import SearchAndFilter from '../../../components/Search/SearchAndFilter';
import Pagination from '../../../components/Footer/Pagination';

const PER_PAGE_KEY = 'purchaseOrderItemsPerPage';
const SELECTED_ROW_KEY = 'purchaseOrderSelectedRow';

const PurchaseOrderTable = ({
    currentItems,
    onRowSelected,
    currentPage,
    setCurrentPage,
    handlePageChange,
    dataMasterList,
    itemsPerPage,
    setItemsPerPage,
    getReport,
    statusOptions = [],
    statusField = 'Doc_Status',
    onStatusChange,
    isStatusEditable = () => true,
    sortConfig,
    onSort,
    bulkMode = false,
    bulkSelected = new Set(),
    onToggleRow = () => { },
    onToggleAll = () => { },
}) => {
    const [itemsPerPageDropdown, setItemsPerPageDropdown] = useState(() => {
        const stored = Number(localStorage.getItem(PER_PAGE_KEY));
        return stored > 0 ? stored : Number(itemsPerPage) || 10;
    });
    const [selectedRow, setSelectedRow] = useState(() => localStorage.getItem(SELECTED_ROW_KEY));
    const [selectedRowIndex, setSelectedRowIndex] = useState(0);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingStatusChange, setPendingStatusChange] = useState(null);
    const [isConfirmBusy, setIsConfirmBusy] = useState(false);
    const [showFileModal, setShowFileModal] = useState(false);
    const [fileModalData, setFileModalData] = useState([]);

    useEffect(() => {
        setItemsPerPage(itemsPerPageDropdown);
        localStorage.setItem(PER_PAGE_KEY, String(itemsPerPageDropdown));
    }, [itemsPerPageDropdown, setItemsPerPage]);

    const handleViewFiles = (files) => {
        if (!Array.isArray(files) || files.length === 0) {
            return;
        }
        setFileModalData(files);
        setShowFileModal(true);
    };

    const confirmPendingChange = useCallback(async () => {
        if (!pendingStatusChange || typeof onStatusChange !== 'function') {
            setShowConfirmModal(false);
            return;
        }
        try {
            setIsConfirmBusy(true);
            await Promise.resolve(onStatusChange(pendingStatusChange.id, pendingStatusChange.newValue, pendingStatusChange.rowData));
        } finally {
            setIsConfirmBusy(false);
            setShowConfirmModal(false);
            setPendingStatusChange(null);
        }
    }, [pendingStatusChange, onStatusChange]);

    useEffect(() => {
        if (!showConfirmModal) return;
        const onKey = (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                if (!isConfirmBusy) confirmPendingChange();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                if (!isConfirmBusy) setShowConfirmModal(false);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [showConfirmModal, isConfirmBusy, confirmPendingChange]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (
                document.activeElement.tagName === 'INPUT' ||
                document.activeElement.tagName === 'TEXTAREA' ||
                document.activeElement.tagName === 'SELECT'
            ) {
                return;
            }

            if (currentItems.length === 0) return;

            switch (event.key) {
                case 'ArrowUp':
                    event.preventDefault();
                    setSelectedRowIndex((prev) => {
                        const newIndex = prev > 0 ? prev - 1 : currentItems.length - 1;
                        const newRowId = currentItems[newIndex]?.Doc_No || newIndex + 1;
                        setSelectedRow(newRowId);
                        localStorage.setItem(SELECTED_ROW_KEY, newRowId);
                        return newIndex;
                    });
                    break;
                case 'ArrowDown':
                    event.preventDefault();
                    setSelectedRowIndex((prev) => {
                        const newIndex = prev < currentItems.length - 1 ? prev + 1 : 0;
                        const newRowId = currentItems[newIndex]?.Doc_No || newIndex + 1;
                        setSelectedRow(newRowId);
                        localStorage.setItem(SELECTED_ROW_KEY, newRowId);
                        return newIndex;
                    });
                    break;
                case 'Enter':
                    event.preventDefault();
                    if (currentItems[selectedRowIndex]) {
                        onRowSelected(currentItems[selectedRowIndex].Doc_No);
                    }
                    break;
                case 'P':
                case 'p':
                    event.preventDefault();
                    if (currentItems[selectedRowIndex] && getReport) {
                        const row = currentItems[selectedRowIndex];
                        const status = row.Doc_Status ?? row.docStatus ?? row.DocStatus;
                        if (String(status) === '13') {
                            return;
                        }
                        getReport(currentItems[selectedRowIndex].Doc_No);
                    }
                    break;
                case 'ArrowLeft':
                    event.preventDefault();
                    if (currentPage > 1) {
                        handlePageChange(currentPage - 1);
                    }
                    break;
                case 'ArrowRight':
                    event.preventDefault();
                    const totalPages = Math.ceil(dataMasterList.length / itemsPerPage);
                    if (currentPage < totalPages) {
                        handlePageChange(currentPage + 1);
                    }
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentItems, selectedRowIndex, currentPage, dataMasterList.length, itemsPerPage, handlePageChange, onRowSelected, getReport]);

    useEffect(() => {
        if (selectedRow && currentItems.length > 0) {
            const index = currentItems.findIndex((item) => item.Doc_No === selectedRow);
            if (index !== -1) {
                setSelectedRowIndex(index);
            }
        }
    }, [selectedRow, currentItems]);

    const handleItemsPerPageChange = (event) => {
        const newVal = Number(event.target.value) || 10;
        setItemsPerPageDropdown(newVal);
        handlePageChange(1);
        setCurrentPage(1);
    };

    const startIndex = dataMasterList.length === 0 ? 0 : (currentPage - 1) * itemsPerPageDropdown + 1;
    const endIndex = Math.min(currentPage * itemsPerPageDropdown, dataMasterList.length);

    const getSortIcon = (columnKey) => {
        if (!sortConfig || sortConfig.key !== columnKey) {
            return (
                <svg className="w-3.5 h-3.5 opacity-60 ms-1" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M5 8l5-5 5 5H5zM15 12l-5 5-5-5h10z" />
                </svg>
            );
        }
        return sortConfig.direction === 'asc' ? (
            <svg className="w-3.5 h-3.5 opacity-90 ms-1" viewBox="0 0 20 20" fill="currentColor">
                <path d="M5 12l5-5 5 5H5z" />
            </svg>
        ) : (
            <svg className="w-3.5 h-3.5 opacity-90 ms-1" viewBox="0 0 20 20" fill="currentColor">
                <path d="M15 8l-5 5-5-5h10z" />
            </svg>
        );
    };

    const STATUS_COLOR_MAP = {
        'รออนุมัติ': '#EAA300',
        'อนุมัติ': '#13A10E',
        'ไม่อนุมัติ': '#DA3B01',
        'ยกเลิก': '#000000',
    };

    const getStatusColor = (rowData, selectedOption, normalizedValue) => {
        const labelFromOption = selectedOption?.label;
        const labelFromRow = rowData?.DocStatus_Name;
        if (labelFromOption && STATUS_COLOR_MAP[labelFromOption]) return STATUS_COLOR_MAP[labelFromOption];
        if (labelFromRow && STATUS_COLOR_MAP[labelFromRow]) return STATUS_COLOR_MAP[labelFromRow];
        if (selectedOption?.color) return selectedOption.color;
        if (normalizedValue === '4' && STATUS_COLOR_MAP['กำลังดำเนินการ']) return STATUS_COLOR_MAP['กำลังดำเนินการ'];
        return rowData?.DocStatus_Colour || '#6c757d';
    };

    const renderStatusControl = (rowData) => {
        if (!Array.isArray(statusOptions) || statusOptions.length === 0 || typeof onStatusChange !== 'function') {
            if (!rowData.DocStatus_Name) {
                return (
                    <center>
                        <p>-</p>
                    </center>
                );
            }
            return (
                <span
                    style={{
                        backgroundColor: getStatusColor(rowData, undefined, undefined),
                        color: '#fff',
                        padding: '6px 12px',
                        borderRadius: '18px',
                        fontWeight: 600,
                        display: 'inline-block',
                        minWidth: '80px',
                        textAlign: 'center',
                        fontSize: '14px'
                    }}
                >
                    {rowData.DocStatus_Name}
                </span>
            );
        }

        const rawValue = rowData[statusField];
        const normalizedValue = rawValue !== undefined && rawValue !== null ? String(rawValue) : '';
        // ถ้าเป็นค่า 4 (ปิดงาน) ให้แสดงเป็น อนุมัติ
        const remappedValue = normalizedValue === '4' ? '2' : normalizedValue;
        const selectedOption = statusOptions.find((option) => String(option.value) === remappedValue) ||
            (normalizedValue === '4' ? { value: '2', label: 'อนุมัติ', color: '#22C55E' } : undefined);
        const isEditable = !bulkMode && isStatusEditable(rowData, selectedOption);

        const handleRequestChange = (newValue) => {
            const toOption = statusOptions.find((o) => String(o.value) === String(newValue)) || (newValue === '4' ? { value: '2', label: 'อนุมัติ', color: '#22C55E' } : undefined);
            setPendingStatusChange({
                id: rowData.Doc_No,
                newValue,
                rowData,
                fromOption: selectedOption,
                toOption,
            });
            setShowConfirmModal(true);
        };

        return (
            <select
                className="form-select form-select-sm"
                value={selectedOption ? String(selectedOption.value) : remappedValue}
                disabled={!isEditable}
                onChange={(event) => handleRequestChange(event.target.value)}
                style={{
                    backgroundColor: getStatusColor(rowData, selectedOption, remappedValue),
                    color: '#fff',
                    fontWeight: 600,
                    borderRadius: '25px',
                    border: 'none',
                    paddingLeft: '12px',
                    paddingRight: '24px',
                    paddingTop: '6px',
                    paddingBottom: '6px',
                    cursor: isEditable ? 'pointer' : 'not-allowed',
                    fontSize: '14px',
                    minWidth: '80px',
                    textAlign: 'center'
                }}
            >
                {statusOptions.map((option) => (
                    <option key={`${rowData.Doc_No}-${option.value}`} value={String(option.value)} style={{ color: '#000' }}>
                        {option.label}
                    </option>
                ))}
            </select>
        );
    };

    return (
        <div className="card table-card">
            <div className="table-responsive table-wrapper">
                <table className="table table-striped table-hover">
                    <thead className="thead-dark">
                        <tr>
                            {bulkMode && (
                                <th className="text-center" style={{ width: '4%' }}>
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        onChange={(e) => onToggleAll(e.target.checked)}
                                        checked={currentItems.length > 0 && currentItems.every((it) => bulkSelected.has(it.Doc_No))}
                                        aria-label="เลือกทั้งหมดในหน้านี้"
                                    />
                                </th>
                            )}
                            <th className="text-center" style={{ width: '8%', cursor: 'pointer' }} onClick={() => onSort && onSort('Doc_Date')}>
                                <span className="d-inline-flex align-items-center justify-content-center w-100">
                                    วันที่ {getSortIcon('Doc_Date')}
                                </span>
                            </th>
                            <th className="text-center" style={{ width: '12%', cursor: 'pointer' }} onClick={() => onSort && onSort('Doc_No')}>
                                <span className="d-inline-flex align-items-center justify-content-center w-100">
                                    เลขที่เอกสาร {getSortIcon('Doc_No')}
                                </span>
                            </th>
                            <th className="text-left" style={{ width: '20%', cursor: 'pointer' }} onClick={() => onSort && onSort('AP_Name')}>
                                <span className="d-inline-flex align-items-center">
                                    ชื่อผู้จำหน่าย {getSortIcon('AP_Name')}
                                </span>
                            </th>
                            <th className="text-center" style={{ width: '10%', cursor: 'pointer' }} onClick={() => onSort && onSort('Doc_DueDate')}>
                                <span className="d-inline-flex align-items-center justify-content-center w-100">
                                    วันครบกำหนดชำระ {getSortIcon('Doc_DueDate')}
                                </span>
                            </th>
                            <th className="text-center" style={{ width: '10%', cursor: 'pointer' }} onClick={() => onSort && onSort('NetTotal')}>
                                <span className="d-inline-flex align-items-center justify-content-center w-100">
                                    ยอดรวมสุทธิ {getSortIcon('NetTotal')}
                                </span>
                            </th>
                            <th className="text-center" style={{ width: '10%', cursor: 'pointer' }} onClick={() => onSort && onSort('Prepayment_Total')}>
                                <span className="d-inline-flex align-items-center justify-content-center w-100">
                                    ยอดชำระล่วงหน้า
                                </span>
                            </th>
                            <th className="text-center" style={{ width: '12%' }}>
                                สถานะ
                            </th>
                            <th className="text-center" style={{ width: '8%' }}>
                                รับสินค้า
                            </th>
                            <th className="text-center" style={{ width: '5%' }}>
                                ไฟล์
                            </th>
                            <th className="text-center" style={{ width: '5%' }}>
                                แก้ไข
                            </th>
                            <th className="text-center" style={{ width: '5%' }}>
                                พิมพ์
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.length > 0 ? (
                            currentItems.map((item, index) => (
                                <tr
                                    key={item.Doc_No || index}
                                    style={{
                                        cursor: 'pointer',
                                        backgroundColor: selectedRow === (item.Doc_No || index)
                                            ? 'rgb(105, 188, 216)'
                                            : 'transparent',
                                        transition: 'background-color 0.3s ease',
                                    }}
                                    onClick={() => {
                                        setSelectedRow(item.Doc_No || index);
                                        setSelectedRowIndex(index);
                                        localStorage.setItem(SELECTED_ROW_KEY, item.Doc_No || index);
                                    }}
                                >
                                    {bulkMode && (
                                        <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={bulkSelected.has(item.Doc_No)}
                                                onChange={(e) => onToggleRow(item.Doc_No, e.target.checked, item)}
                                                aria-label={`เลือก ${item.Doc_No}`}
                                            />
                                        </td>
                                    )}
                                    <td className="text-center">
                                        {item.Doc_Date ? formatDate(item.Doc_Date) : '-'}
                                    </td>
                                    <td className="text-center">{item.Doc_No || '-'}</td>
                                    <td className="text-left">{item.AP_Name || '-'}</td>
                                    <td className="text-center">
                                        {item.Doc_DueDate ? formatDate(item.Doc_DueDate) : '-'}
                                    </td>
                                    <td className="text-end">
                                        {formatCurrency(parseCurrency(item.NetTotal) || 0)}
                                    </td>
                                    <td className="text-end">
                                        {item.Prepayment_Total && Number(item.Prepayment_Total) > 0 ? (
                                            formatCurrency(item.Prepayment_Total)
                                        ) : (
                                            item.Prepayment_TotalFmt || '-'
                                        )}
                                    </td>
                                    <td className="text-left">{renderStatusControl(item)}</td>
                                    <td className="text-center">
                                        {item.Doc_Status_ReceiveName ? (
                                            <span
                                                style={{
                                                    backgroundColor: item.Doc_Status_ReceiveColour || '#6c757d',
                                                    color: '#fff',
                                                    padding: '6px 12px',
                                                    borderRadius: '18px',
                                                    fontWeight: 600,
                                                    display: 'inline-block',
                                                    width: '100%',
                                                    boxSizing: 'border-box',
                                                    textAlign: 'center',
                                                    fontSize: '14px'
                                                }}
                                            >
                                                {item.Doc_Status_ReceiveName}
                                            </span>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                    <td className="text-center">
                                        {Array.isArray(item.File_Attachments) && item.File_Attachments.length > 0 ? (
                                            <button
                                                className="btn btn-link btn-sm px-3"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleViewFiles(item.File_Attachments);
                                                }}
                                            >
                                                <i className="fas fa-paperclip" style={{ fontSize: '16px', color: 'rgb(239, 108, 0)' }}></i>
                                            </button>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                    <td className="text-center">
                                        <button
                                            className="btn btn-link btn-sm px-3"
                                            onClick={() => onRowSelected(item.Doc_No)}
                                        >
                                            <i className="fa fa-edit" style={{ fontSize: '16px', color: 'rgb(239, 108, 0)' }}></i>
                                        </button>
                                    </td>
                                    <td className="text-center">
                                        {(() => {
                                            const status = item.Doc_Status ?? item.docStatus ?? item.DocStatus;
                                            const isCancelled = String(status) === '13';
                                            return (
                                                <button
                                                    className="btn btn-link btn-sm px-3"
                                                    onClick={() => {
                                                        if (isCancelled) return;
                                                        getReport(item.Doc_No);
                                                    }}
                                                    disabled={isCancelled}
                                                    style={isCancelled ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                                                >
                                                    <i className="fa fa-print" style={{ fontSize: '16px' }}></i>
                                                </button>
                                            );
                                        })()}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={bulkMode ? 12 : 11}>
                                    <center>
                                        <h5>ไม่พบข้อมูล</h5>
                                    </center>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Footer: hide completely in bulk mode to avoid overlap with bulk footer */}
            {!bulkMode && (
                <div className="table-footer-sticky d-flex justify-content-between align-items-center p-3">
                    <div>
                        <select
                            value={itemsPerPageDropdown}
                            onChange={handleItemsPerPageChange}
                            className="form-select"
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={30}>30</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                    <div>
                        <span>
                            แสดง {startIndex}-{endIndex} ของ {dataMasterList.length.toLocaleString()} รายการ
                        </span>
                    </div>
                    <div className="card-footer bg-light p-2">
                        <small className="text-muted">
                            <i className="fas fa-keyboard me-1"></i>
                            <strong>คีย์ลัด:</strong> ↑↓ เลือกแถว | ←→ เปลี่ยนหน้า | Enter เข้าดู | P พิมพ์
                        </small>
                    </div>
                    <Pagination
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        itemsPerPage={itemsPerPageDropdown}
                        dataMasterList={dataMasterList}
                        handlePageChange={handlePageChange}
                    />
                </div>
            )}

            {/* Confirm Status Change Modal */}
            <div className={`modal fade ${showConfirmModal ? 'show' : ''}`} tabIndex="-1" style={{ display: showConfirmModal ? 'block' : 'none' }}>
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title fw-bold">ยืนยันการเปลี่ยนสถานะ</h5>
                            <button type="button" className="btn-close" onClick={() => setShowConfirmModal(false)} aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <p>
                                คุณแน่ใจว่าต้องการเปลี่ยนสถานะจาก
                                <strong className="mx-1">{pendingStatusChange?.fromOption?.label || '-'}</strong>
                                เป็น
                                <strong className="mx-1">{pendingStatusChange?.toOption?.label || '-'}</strong>
                                หรือไม่?
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-lg w-25 shadow text-white d-flex align-items-center justify-content-center"
                                style={{ backgroundColor: '#E13F57', fontSize: '16px' }}
                                onClick={async () => {
                                    if (!pendingStatusChange || typeof onStatusChange !== 'function') {
                                        setShowConfirmModal(false);
                                        return;
                                    }
                                    try {
                                        setIsConfirmBusy(true);
                                        await Promise.resolve(onStatusChange(pendingStatusChange.id, pendingStatusChange.newValue, pendingStatusChange.rowData));
                                    } finally {
                                        setIsConfirmBusy(false);
                                        setShowConfirmModal(false);
                                        setPendingStatusChange(null);
                                    }
                                }}
                                disabled={isConfirmBusy}
                            >
                                {isConfirmBusy ? (
                                    <>
                                        <i className="fa fa-spinner fa-spin me-2" style={{ fontSize: '20px', color: '#ffffff' }} aria-hidden="true"></i>
                                        กำลังโหลด
                                    </>
                                ) : (
                                    'ยืนยัน'
                                )}
                            </button>
                            <button
                                type="button"
                                className="btn btn-lg w-30 shadow text-white"
                                style={{ backgroundColor: 'red', fontSize: '16px' }}
                                onClick={() => setShowConfirmModal(false)}
                                disabled={isConfirmBusy}
                            >
                                ยกเลิก
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {showFileModal && (
                <FileModal
                    mode="S"
                    openUploadAttach={false}
                    setOpenUploadAttach={() => { }}
                    showViewAttachModal={showFileModal}
                    setShowViewAttachModal={setShowFileModal}
                    selectedFiles={fileModalData}
                    selectedRecFiles={[]}
                    setSelectedFiles={() => { }}
                    handleRemoveImage={() => { }}
                    handleInputFileChange={() => { }}
                    handleBeforeUploadAttach={() => { }}
                />
            )}
        </div>
    );
};

function Main({ masterList, detailList, statusColours, statusPaidColours, statusReceiveColours, name, onPageInsert, onRowSelected, getReport, statusOptions = [], onStatusChange, isStatusEditable }) {
    const [enrichedMasterList, setEnrichedMasterList] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(() => {
        const stored = Number(localStorage.getItem(PER_PAGE_KEY));
        return stored > 0 ? stored : Number(localStorage.getItem('itemsPerPageDropdown')) || 10;
    });
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [searchTerm, setSearchTerm] = useState(localStorage.getItem('searchTerm') || '');
    const [startDate, setStartDate] = useState(() => {
        const storedStartDate = localStorage.getItem('startDate');
        return storedStartDate || moment().startOf('month').format('YYYY-MM-DD');
    });
    const [endDate, setEndDate] = useState(() => {
        const storedEndDate = localStorage.getItem('endDate');
        return storedEndDate || moment().endOf('month').format('YYYY-MM-DD');
    });
    const [selectedStatus] = useState(null);
    const [dateType] = useState('1');
    const [bulkMode, setBulkMode] = useState(false);
    const [bulkSelected, setBulkSelected] = useState(() => new Set());
    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [bulkAction, setBulkAction] = useState(null);
    const [isBulkBusy, setIsBulkBusy] = useState(false);

    const normalizedStatusOptions = Array.isArray(statusOptions) ? statusOptions : [];
    const handleStatusChangeProp = typeof onStatusChange === 'function' ? onStatusChange : () => { };
    const isStatusEditableProp = typeof isStatusEditable === 'function' ? isStatusEditable : () => true;

    const initialize = useCallback(async () => {
        try {
            if (masterList && masterList.length > 0) {
                const uploadRecords = await getDataWithComp(
                    "Upload_File_List",
                    `AND DocCode_ID = 2`
                );

                const payDRecords = await getData(
                    "PAY_D",
                    `1=1`
                );
                const payHRecords = await getDataWithComp(
                    "PAY_H",
                    `AND 1=1`
                );

                const enriched = masterList.map((item) => {
                    const docId =
                        item.Doc_Id ??
                        item.Doc_ID ??
                        item.docId ??
                        item.doc_id ??
                        null;
                    const normalizedDocId = docId !== null && docId !== undefined ? Number(docId) : null;

                    const attachments =
                        Array.isArray(uploadRecords) && normalizedDocId !== null
                            ? uploadRecords.filter(
                                (file) => Number(file.Doc_ID) === normalizedDocId
                            )
                            : [];

                    let prepaySum = 0;
                    if (Array.isArray(payDRecords) && normalizedDocId !== null) {
                        const matchedPayDs = payDRecords.filter((pd) => {
                            try {
                                return Number(pd.Rec_Id) === normalizedDocId;
                            } catch (e) {
                                return String(pd.Rec_Id) === String(normalizedDocId);
                            }
                        });
                        const payIds = Array.from(new Set(matchedPayDs.map((pd) => pd.Pay_Id).filter((id) => id !== undefined && id !== null)));
                        if (payIds.length > 0 && Array.isArray(payHRecords)) {
                            payIds.forEach((pid) => {
                                const ph = payHRecords.find((h) => String(h.Pay_Id) === String(pid));
                                if (ph && ph.Total_Pay_NetTotal) {
                                    prepaySum += Number(parseCurrency(ph.Total_Pay_NetTotal) || 0);
                                }
                            });
                        }
                    }

                    return {
                        ...item,
                        File_Attachments: attachments,
                        Prepayment_Total: prepaySum,
                        Prepayment_TotalFmt: prepaySum > 0 ? formatCurrency(prepaySum) : null,
                    };
                });

                setEnrichedMasterList(enriched);
                setFilteredItems(enriched);
            } else {
                setEnrichedMasterList([]);
                setFilteredItems([]);
            }
        } catch (error) {
            getAlert('FAILED', error.message);
        }
    }, [masterList]);

    useEffect(() => {
        initialize();
    }, [initialize]);

    const filterItems = useCallback(() => {
        const sourceList = enrichedMasterList.length > 0 ? enrichedMasterList : (masterList || []);
        let filtered = [...sourceList];
        const start = moment(startDate, 'YYYY-MM-DD');
        const end = moment(endDate, 'YYYY-MM-DD');

        filtered = filtered.filter((item) => {
            const itemDate = dateType === '1'
                ? moment(item.Doc_Date, 'YYYY-MM-DD')
                : moment(item.Doc_DueDate, 'YYYY-MM-DD');
            return itemDate.isBetween(start, end, null, '[]');
        });

        if (searchTerm.trim() !== '') {
            const lowercasedSearchTerm = searchTerm.toLowerCase();
            filtered = filtered.filter((item) =>
                Object.values(item).some((value) => {
                    if (typeof value === 'string') {
                        return value.toLowerCase().includes(lowercasedSearchTerm);
                    }
                    if (typeof value === 'number') {
                        return value.toString().includes(searchTerm);
                    }
                    return false;
                })
            );
        }

        if (selectedStatus) {
            filtered = filtered.filter((item) => {
                if (statusColours.some((status) => status.DocSetStaus_Name === selectedStatus)) {
                    return item.DocStatus_Name === selectedStatus;
                }
                if (statusPaidColours.some((status) => status.DocSetStaus_Name === selectedStatus)) {
                    return item.Doc_Status_PaidName === selectedStatus;
                }
                if (statusReceiveColours.some((status) => status.DocSetStaus_Name === selectedStatus)) {
                    return item.Doc_Status_ReceiveName === selectedStatus;
                }
                return false;
            });
        }

        if (bulkMode) {
            filtered = filtered.filter((item) => item.DocStatus_Name === 'รออนุมัติ');
        }

        setFilteredItems(filtered);
    }, [enrichedMasterList, masterList, startDate, endDate, dateType, searchTerm, selectedStatus, statusColours, statusPaidColours, statusReceiveColours, bulkMode]);

    useEffect(() => {
        setCurrentPage(1);
        filterItems();
    }, [filterItems]);


    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const sortedItems = React.useMemo(() => {
        if (!sortConfig || !sortConfig.key) return filteredItems;
        const key = sortConfig.key;
        const direction = sortConfig.direction === 'desc' ? -1 : 1;
        const copy = [...filteredItems];
        copy.sort((a, b) => {
            const av = a[key];
            const bv = b[key];

            const dateKeys = ['Doc_Date', 'Doc_DueDate'];
            if (dateKeys.includes(key)) {
                const am = av ? moment(av) : null;
                const bm = bv ? moment(bv) : null;
                if (!am && !bm) return 0;
                if (!am) return 1 * direction;
                if (!bm) return -1 * direction;
                if (am.isBefore(bm)) return -1 * direction;
                if (am.isAfter(bm)) return 1 * direction;
                return 0;
            }

            const numKeys = ['NetTotal'];
            if (numKeys.includes(key)) {
                const an = parseCurrency(av) || 0;
                const bn = parseCurrency(bv) || 0;
                return an === bn ? 0 : an < bn ? -1 * direction : 1 * direction;
            }

            const as = (av ?? '').toString().toLowerCase();
            const bs = (bv ?? '').toString().toLowerCase();
            if (as < bs) return -1 * direction;
            if (as > bs) return 1 * direction;
            return 0;
        });
        return copy;
    }, [filteredItems, sortConfig]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = sortedItems.slice(indexOfFirstItem, indexOfLastItem);

    const handleItemsPerPageChangeMain = (event) => {
        const newVal = Number(event.target.value) || 10;
        setItemsPerPage(newVal);
        setCurrentPage(1);
        localStorage.setItem(PER_PAGE_KEY, String(newVal));
    };

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
        setCurrentPage(1);
    };

    const toggleBulkMode = () => {
        setBulkMode((prev) => !prev);
        setBulkSelected(new Set());
    };

    const handleToggleRow = (docNo, checked) => {
        setBulkSelected((prev) => {
            const next = new Set(prev);
            if (checked) next.add(docNo); else next.delete(docNo);
            return next;
        });
    };

    const handleToggleAll = (checked) => {
        if (!checked) {
            setBulkSelected(new Set());
            return;
        }
        const next = new Set(bulkSelected);
        currentItems.forEach((it) => next.add(it.Doc_No));
        setBulkSelected(next);
    };

    const resolveStatusValue = (label) => {
        const opt = (statusOptions || []).find((o) => o.label === label);
        if (opt) return String(opt.value);

        const contains = (statusOptions || []).find((o) => o.label && o.label.includes(label));
        return contains ? String(contains.value) : null;
    };

    const openBulkConfirm = (action) => {
        if (bulkSelected.size === 0) return;
        setBulkAction(action);
        setBulkModalOpen(true);
    };

    const applyBulkAction = async () => {
        if (!onStatusChange || bulkSelected.size === 0) {
            setBulkModalOpen(false);
            return;
        }
        let targetLabel = '';
        if (bulkAction === 'approve') targetLabel = 'อนุมัติ';
        else if (bulkAction === 'reject') targetLabel = 'ไม่อนุมัติ';
        else if (bulkAction === 'cancel') targetLabel = 'ยกเลิก';
        const targetValue = resolveStatusValue(targetLabel);
        if (!targetValue) {
            getAlert('FAILED', 'ไม่พบสถานะปลายทางสำหรับการปรับสถานะ');
            setBulkModalOpen(false);
            return;
        }
        try {
            setIsBulkBusy(true);
            const baseRows = enrichedMasterList.length > 0 ? enrichedMasterList : (masterList || []);
            const rowsByKey = new Map(baseRows.map((r) => [r.Doc_No, r]));
            const promises = Array.from(bulkSelected).map((docNo) => onStatusChange(docNo, targetValue, rowsByKey.get(docNo)));
            await Promise.all(promises);
            setBulkSelected(new Set());
            setBulkModalOpen(false);
            setBulkMode(false);
            setCurrentPage(1);
            if (typeof window !== 'undefined' && window.location) {
                setTimeout(() => window.location.reload(), 50);
            }
        } finally {
            setIsBulkBusy(false);
        }
    };

    return (
        <>
            <Topbar
                breadcrumbs={[
                    { label: 'จัดซื้อสินค้า', link: '/purchase-order' },
                    { label: name },
                ]}
            />
            <div className="page-header d-flex justify-content-between align-items-center mb-0">
                <Breadcrumbs page={name} items={[
                    { name: 'จัดซื้อสินค้า', url: '/purchase-order' },
                    { name: name, url: '/purchase-order' },
                ]} />
            </div>
            <div className="row">
                <div className="col-12">
                    <SearchAndFilter
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        searchPlaceholder="ค้นหาเลขเอกสาร, ผู้จำหน่าย, พนักงาน, แผนก..."
                        startDate={startDate}
                        endDate={endDate}
                        setStartDate={setStartDate}
                        setEndDate={setEndDate}
                        showDateFilter={true}
                        onAddNew={onPageInsert}
                        addButtonText={`สร้าง${name}`}
                        showAddButton={true}
                        name={name}
                        filteredItems={filteredItems}
                        bulkMode={bulkMode}
                        onToggleBulk={toggleBulkMode}
                    />
                    <PurchaseOrderTable
                        currentItems={currentItems}
                        onRowSelected={onRowSelected}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        handlePageChange={handlePageChange}
                        dataMasterList={sortedItems}
                        itemsPerPage={itemsPerPage}
                        setItemsPerPage={setItemsPerPage}
                        getReport={(docNo) => getReport('po', docNo)}
                        statusOptions={normalizedStatusOptions}
                        statusField="Doc_Status"
                        onStatusChange={handleStatusChangeProp}
                        isStatusEditable={isStatusEditableProp}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        bulkMode={bulkMode}
                        bulkSelected={bulkSelected}
                        onToggleRow={handleToggleRow}
                        onToggleAll={handleToggleAll}
                    />
                </div>
            </div>

            {/* Bulk action bar in footer area */}
            {bulkMode && (
                <div className="table-footer-sticky d-flex justify-content-between align-items-center p-3">
                    {/* Left: keep page size & count visible in bulk mode */}
                    <div className="d-flex align-items-center gap-3">
                        <select
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChangeMain}
                            className="form-select"
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={30}>30</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>

                    {/* Right: bulk selection & actions */}
                    <div className="d-flex align-items-center gap-3">
                        <span className="me-3">เลือก {bulkSelected.size} รายการ</span>
                        <button
                            type="button"
                            className="btn btn-lg btn-success"
                            disabled={bulkSelected.size === 0}
                            onClick={() => openBulkConfirm('approve')}
                            style={{ minWidth: '120px', padding: '8px 18px', fontSize: '16px', borderRadius: '1.5rem', fontWeight: 'bold' }}
                        >
                            อนุมัติ
                        </button>
                        <button
                            type="button"
                            className="btn btn-lg btn-warning"
                            disabled={bulkSelected.size === 0}
                            onClick={() => openBulkConfirm('reject')}
                            style={{ minWidth: '120px', padding: '8px 18px', fontSize: '16px', borderRadius: '1.5rem', fontWeight: 'bold' }}
                        >
                            ไม่อนุมัติ
                        </button>
                        <button
                            type="button"
                            className="btn btn-lg btn-danger"
                            disabled={bulkSelected.size === 0}
                            onClick={() => openBulkConfirm('cancel')}
                            style={{ minWidth: '120px', padding: '8px 18px', fontSize: '16px', borderRadius: '1.5rem', fontWeight: 'bold' }}
                        >
                            ยกเลิก
                        </button>
                    </div>
                </div>
            )}

            {/* Bulk confirm modal */}
            <div className={`modal fade ${bulkModalOpen ? 'show' : ''}`} tabIndex="-1" style={{ display: bulkModalOpen ? 'block' : 'none' }}>
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title fw-bold">ยืนยันการปรับสถานะหลายรายการ</h5>
                            <button type="button" className="btn-close" onClick={() => setBulkModalOpen(false)} aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <p>
                                คุณต้องการ{bulkAction === 'approve' ? 'อนุมัติ' : bulkAction === 'reject' ? 'ไม่อนุมัติ' : 'ยกเลิก'} จำนวน {bulkSelected.size} รายการใช่หรือไม่?
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-lg w-25 shadow text-white d-flex align-items-center justify-content-center"
                                style={{ backgroundColor: '#E13F57', fontSize: '16px' }}
                                onClick={applyBulkAction}
                                disabled={isBulkBusy}
                            >
                                {isBulkBusy ? (
                                    <>
                                        <i className="fa fa-spinner fa-spin me-2" style={{ fontSize: '20px', color: '#ffffff' }} aria-hidden="true"></i>
                                        กำลังโหลด
                                    </>
                                ) : (
                                    'ยืนยัน'
                                )}
                            </button>
                            <button
                                type="button"
                                className="btn btn-lg w-30 shadow text-white"
                                style={{ backgroundColor: 'red', fontSize: '16px' }}
                                onClick={() => setBulkModalOpen(false)}
                                disabled={isBulkBusy}
                            >
                                ยกเลิก
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Main;
